/**
 * Facility Management Component
 * 
 * Main component for managing facilities. Handles CRUD operations,
 * URL-based filtering, and coordinates between list and form views.
 * 
 * @component
 * @example
 * return <Facility />
 */

import React, { useEffect, useState, useCallback } from "react";
import { message } from "antd";
import { useSearchParams } from "react-router-dom";
import CustomContainer from "../common/CustomContainer";
import FacilityForm from "./facility-form";
import FacilityList from "./facility-list";
import DepartmentAllocation from "./DepartmentAllocation";
import FacilityService from "../../services/facility-service";
import { formatPhoneNumber, formatPhoneNumberForEdit } from "../../utils/helpers/format-phone-number";
import { insuranceOptions, networkPlanTypes } from "../../utils/constants/insuranceOptions";
import { useURLFiltering, useCRUDOperations } from "../../custom-hooks";

/**
 * Helper function to map API insurance/company names to option values
 * 
 * @param {string} label - Insurance company label
 * @returns {string} Mapped value
 */
const insuranceLabelToValue = (label) => {
  if (!label) return "";
  // Try to find a match ignoring case and common typos
  const normalized = String(label)
    .toLowerCase()
    .replace(/[^a-z]/g, "");
  const found = insuranceOptions.find(
    (opt) =>
      opt.label.toLowerCase().replace(/[^a-z]/g, "") === normalized ||
      opt.value.toLowerCase().replace(/[^a-z]/g, "") === normalized
  );
  return found ? found.value : label.toLowerCase();
};

/**
 * Helper function to map plan type labels to values
 * 
 * @param {string} label - Plan type label
 * @returns {string} Mapped value
 */
const planTypeLabelToValue = (label) => {
  const found = networkPlanTypes.find(
    (opt) => opt.label.toLowerCase() === String(label).toLowerCase()
  );
  return found ? found.value : label;
};

/**
 * Maps API facility object to form field names
 * 
 * @param {Object} facility - Facility object from API
 * @returns {Object} Mapped facility data for form
 */
function mapFacilityToFormValues(facility) {
  const mappedInsValues = Array.isArray(facility.inNetworkInsurancePlans)
    ? facility.inNetworkInsurancePlans.map(insuranceLabelToValue)
    : [];
  const mappedPlanTypeValues = Array.isArray(
    facility.inNetworkInsurancePlanType
  )
    ? facility.inNetworkInsurancePlanType.map(planTypeLabelToValue)
    : facility.inNetworkInsurancePlanType
    ? [planTypeLabelToValue(facility.inNetworkInsurancePlanType)]
    : [];
  
  // Map place of service from API format to form format (single value)
  const mappedPlaceOfServiceValue = facility.placeOfService || "";
  
  // Map departments from API format to form format
  console.log('🏥 Processing departments in mapFacilityToFormValues:', facility.departments);
  const mappedDepartments = Array.isArray(facility.departments) && facility.departments.length > 0
    ? facility.departments.map(dept => ({
        id: dept._id || dept.id || undefined, // Preserve ID for existing departments
        name: dept.name || "",
        faxNumber: dept.faxNumber ? formatPhoneNumberForEdit(dept.faxNumber) : ""
      }))
    : [{ name: "", faxNumber: "" }]; // Default single empty department if none exist
  
  console.log('🔧 Mapped departments for form:', mappedDepartments);
    
  const formValues = {
    facilityName: facility.facilityName || "",
    phoneNumber: formatPhoneNumberForEdit(facility.phoneNumber) || "",
    extension: facility.extension || "",
    faxNumber: formatPhoneNumberForEdit(facility.faxNumber) || "",
    groupNPI: facility.groupNPI || "",
    taxId: facility.groupTaxId || "",
    inNetworkIns: mappedInsValues,
    networkPlanType: mappedPlanTypeValues,
    placeOfService: mappedPlaceOfServiceValue,
    streetAddress: facility.location?.streetAddress || "",
    city: facility.location?.city || "",
    state: facility.location?.state || "",
    zipCode: facility.location?.zipcode || "",
    departments: mappedDepartments,
  };
  
  console.log('📋 Final form values being returned:', formValues);
  return formValues;
}

/**
 * Facility Management Main Component
 */
const Facility = () => {
  // Use custom hooks for common functionality
  const {
    selectedFilters,
    updateFilters,
    getAPIParams
  } = useURLFiltering('facility');

  const {
    data: facilities,
    loading,
    submitting,
    renderType,
    initialValues,
    fetchData,
    refreshData,
    handleSubmit: handleCRUDSubmit,
    handleEdit: handleCRUDEdit,
    handleAdd,
    handleCancel,
    setRenderType
  } = useCRUDOperations(FacilityService, 'facility');

  // Department allocation state management
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFacility, setSelectedFacility] = useState(null);
  const isDepartmentAllocationMode = searchParams.get('departmentAllocation') === 'true';
  const facilityId = searchParams.get('facilityId');

  /**
   * Handles filter changes with API integration
   * 
   * @param {Object} newFilters - New filter values
   */
  const handleFiltersChange = (newFilters) => {
    try {
      updateFilters(newFilters); // This will trigger useEffect to call fetchData
    } catch (error) {
      console.error('Error applying filters:', error);
      message.error('Failed to apply filters. Please try again.');
    }
  };

  /**
   * Refreshes facility data with current filters
   * 
   * @returns {Promise<Array>} Refreshed facility data
   */
  const handleRefresh = async () => {
    const apiParams = getAPIParams(selectedFilters);
    return await refreshData(apiParams);
  };

  /**
   * Handles editing a facility - fetches complete details with departments
   * 
   * @param {Object} facility - Facility object from list (contains basic info and ID)
   */
  const handleEdit = async (facility) => {
    try {
      console.log('🔄 Fetching complete facility details for edit:', facility._id);
      
      // Fetch complete facility details with departments
      const response = await FacilityService.getById(facility._id);
      
      if (response.success && response.data) {
        console.log('✅ Successfully fetched facility details with departments:', response.data);
        console.log('🏥 Departments in response.data:', response.data.departments);
        // Use the complete facility data with departments for editing
        handleCRUDEdit(response.data, mapFacilityToFormValues);
      } else {
        console.error('❌ Failed to fetch facility details:', response.message);
        // Fallback to using the facility data from the list (without departments)
        console.log('⚠️ Falling back to list data without departments');
        handleCRUDEdit(facility, mapFacilityToFormValues);
      }
    } catch (error) {
      console.error('❌ Error fetching facility details for edit:', error);
      // Fallback to using the facility data from the list (without departments)
      console.log('⚠️ Falling back to list data due to error');
      handleCRUDEdit(facility, mapFacilityToFormValues);
    }
  };

  /**
   * Handles form submission for create/update
   * 
   * @param {Object} formData - Form data to submit
   */
  const handleSubmit = async (formData) => {
    // Transform departments from form format to API format - filter out empty departments
    const transformedDepartments = Array.isArray(formData.departments) 
      ? formData.departments
          .filter(dept => (dept.name && dept.name.trim() !== '') || (dept.faxNumber && dept.faxNumber.trim() !== '')) // Include if has name OR fax
          .map(dept => {
            const department = {
              name: dept.name ? dept.name.trim() : '',
              faxNumber: dept.faxNumber ? formatPhoneNumber(dept.faxNumber) : null
            };
            // Include id for existing departments (those that have an id)
            if (dept.id) {
              department.id = dept.id;
            }
            return department;
          })
      : [];

    // Transform form data to API format
    const facilityData = {
      facilityName: formData.facilityName,
      location: {
        streetAddress: formData.streetAddress,
        city: formData.city,
        state: formData.state,
        zipcode: formData.zipCode,
      },
      phoneNumber: formatPhoneNumber(formData.phoneNumber),
      faxNumber: formatPhoneNumber(formData.faxNumber),
      extension: formData.extension,
      groupNPI: formData.groupNPI,
      groupTaxId: formData.taxId,
      inNetworkInsurancePlans: formData.inNetworkIns || [],
      inNetworkInsurancePlanType: formData.networkPlanType || [],
      placeOfService: formData.placeOfService,
      departments: transformedDepartments,
    };

    const result = await handleCRUDSubmit(facilityData, handleRefresh);
    return result;
  };

  /**
   * Handles adding a new facility - clears form state
   */
  const handleAddNew = () => {
    handleAdd(); // This will clear initialValues and set renderType to 'form'
  };

  /**
   * Handles back navigation
   */
  const handleBack = () => {
    handleCancel();
  };

  /**
   * Closes department allocation mode and returns to facility list
   */
  const handleCloseDepartmentAllocation = useCallback(() => {
    setSearchParams(prevParams => {
      const newParams = new URLSearchParams(prevParams);
      newParams.delete('departmentAllocation');
      newParams.delete('facilityId');
      newParams.delete('departmentId');
      return newParams;
    });
    setSelectedFacility(null);
  }, [setSearchParams]);

  /**
   * Handles saving department allocations
   */
  const handleSaveDepartmentAllocation = async (allocationData) => {
    try {
      console.log('Saving department allocation:', allocationData);
      // TODO: Implement API call to save department allocations
      message.success('Department allocation saved successfully!');
      handleCloseDepartmentAllocation();
    } catch (error) {
      console.error('Error saving department allocation:', error);
      message.error('Failed to save department allocation. Please try again.');
    }
  };

  // Initialize data on component mount
  useEffect(() => {
    const apiParams = getAPIParams(selectedFilters);
    fetchData(apiParams);
  }, [selectedFilters, fetchData, getAPIParams]);

  // Handle department allocation mode - fetch facility details when facilityId is present
  useEffect(() => {
    const fetchFacilityForAllocation = async () => {
      if (isDepartmentAllocationMode && facilityId && !selectedFacility) {
        try {
          console.log('🔄 Fetching facility details for department allocation:', facilityId);
          const response = await FacilityService.getById(facilityId);
          
          if (response.success && response.data) {
            console.log('✅ Successfully fetched facility for allocation:', response.data);
            setSelectedFacility(response.data);
          } else {
            console.error('❌ Failed to fetch facility for allocation:', response.message);
            message.error('Failed to load facility details. Returning to facility list.');
            handleCloseDepartmentAllocation();
          }
        } catch (error) {
          console.error('❌ Error fetching facility for allocation:', error);
          message.error('Failed to load facility details. Returning to facility list.');
          handleCloseDepartmentAllocation();
        }
      } else if (!isDepartmentAllocationMode && selectedFacility) {
        // Clean up when leaving department allocation mode
        setSelectedFacility(null);
      }
    };

    fetchFacilityForAllocation();
  }, [isDepartmentAllocationMode, facilityId, selectedFacility, handleCloseDepartmentAllocation]);

  // Determine container width based on current view
  const getContainerWidth = () => {
    if (isDepartmentAllocationMode) return "95%";
    return renderType === "list" ? "90%" : "80%";
  };

  return (
    <CustomContainer containerWidth={getContainerWidth()}>
      {isDepartmentAllocationMode ? (
        <DepartmentAllocation
          facilityData={selectedFacility}
          onClose={handleCloseDepartmentAllocation}
          onSave={handleSaveDepartmentAllocation}
          loading={!selectedFacility}
        />
      ) : renderType === "list" ? (
        <FacilityList
          setInitialValues={() => {}} // Not used anymore, handled by hooks
          setRenderType={setRenderType}
          renderType={renderType}
          facilities={facilities}
          loading={loading}
          onEdit={handleEdit}
          onAdd={handleAddNew}
          setSelectedFacilityId={() => {}} // Not used anymore, handled by hooks
          onRefresh={handleRefresh}
          selectedFilters={selectedFilters}
          onFiltersChange={handleFiltersChange}
        />
      ) : (
        <FacilityForm
          onSubmit={handleSubmit}
          handleBack={handleBack}
          setRenderType={setRenderType}
          renderType={renderType}
          initialValues={initialValues}
          submitting={submitting}
        />
      )}
    </CustomContainer>
  );
};

export default Facility;